import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    Button,
    Avatar,
    Tabs,
    Tab,
    IconButton,
    InputAdornment,
    CircularProgress
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    getAdminProfile,
    updateUserProfile,
    changeAdminPassword,
    clearProfileMessages
} from '../../Redux/Reducers/ProfileReducer';
import { Visibility, VisibilityOff, Email, Phone, Person, CloudUpload } from '@mui/icons-material';
import { getFullUrl } from '../../utils/globalUtils';

function Profile() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [editErrors, setEditErrors] = useState({});
    const [passwordErrors, setPasswordErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(null);

    const { profile, loading, error, successMessage } = useSelector((state) => state.ProfileReducer);
    const [tabValue, setTabValue] = useState(0);


    const [editForm, setEditForm] = useState({
        name: '',
        phone: '',
        profileImage: null
    });

    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const validateEditProfile = () => {
        const errors = {};

        if (!editForm.name.trim()) {
            errors.name = t('update_profile.change_profile.validation.name_required', 'Name is required');
        } else if (editForm.name.trim().length < 3) {
            errors.name = t('update_profile.change_profile.validation.name_min_length', 'Name must be at least 3 characters');
        }

        if (!editForm.phone.trim()) {
            errors.phone = t('update_profile.change_profile.validation.phone_required', 'Phone number is required');
        } else {
            const phoneRegex = /^[0-9+\-\s]{7,15}$/;
            if (!phoneRegex.test(editForm.phone.trim())) {
                errors.phone = t('update_profile.change_profile.validation.invalid_phone', 'Please enter a valid phone number');
            }
        }

        if (editForm.profileImage instanceof File) {
            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

            if (!allowedTypes.includes(editForm.profileImage.type)) {
                errors.profileImage = t('update_profile.change_profile.validation.image_type', 'Only JPG, JPEG and PNG files are allowed');
            }

            if (editForm.profileImage.size > 2 * 1024 * 1024) {
                errors.profileImage = t('update_profile.change_profile.validation.image_size', 'Image must be less than 2MB');
            }
        }

        setEditErrors(errors);
        return Object.keys(errors).length === 0;
    };



    useEffect(() => {
        dispatch(getAdminProfile());
    }, [dispatch]);

    useEffect(() => {
        if (profile) {
            setEditForm({
                name: profile.name || '',
                phone: profile.phone || '',
                profileImage: null
            });
            if (profile.profileImage) {
                setImagePreview(profile.profileImage);
            }
        }
    }, [profile]);

    useEffect(() => {
        if (successMessage) {
            toast.success(t(successMessage));
            dispatch(clearProfileMessages());
        }
        if (error) {
            toast.error(t(error));
            dispatch(clearProfileMessages());
        }
    }, [successMessage, error, dispatch, t]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        const finalValue = name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value;
        setEditForm(prev => ({ ...prev, [name]: finalValue }));
        setEditErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setEditErrors(prev => ({ ...prev, profileImage: '' }));
        if (file) {
            setEditForm(prev => ({ ...prev, profileImage: file }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm(prev => ({ ...prev, [name]: value }));

        setPasswordErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    const handleEditSubmit = (e) => {
        e.preventDefault();
        if (!validateEditProfile()) return;
        const payload = new FormData();
        payload.append("name", editForm.name);
        payload.append("phone", editForm.phone);
        if (editForm.profileImage instanceof File) {
            payload.append("profileImage", editForm.profileImage);
        }
        dispatch(updateUserProfile(payload));
    };

    const validatePasswordForm = () => {
        const errors = {};

        const passwordRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;

        if (!passwordForm.currentPassword?.trim()) {
            errors.currentPassword = t(
                'update_profile.change_password_form.validation.current_password_required',
                'Current password is required'
            );
        }

        if (!passwordForm.newPassword?.trim()) {
            errors.newPassword = t(
                'update_profile.change_password_form.validation.new_password_required',
                'New password is required'
            );
        } else if (passwordForm.newPassword.length < 8) {
            errors.newPassword = t(
                'update_profile.change_password_form.validation.password_min_length',
                'Password must be at least 8 characters'
            );
        } else if (!passwordRegex.test(passwordForm.newPassword)) {
            errors.newPassword = t(
                'update_profile.change_password_form.validation.password_pattern',
                'Password must contain uppercase, lowercase, number and special character'
            );
        } else if (passwordForm.currentPassword === passwordForm.newPassword) {
            errors.newPassword = t(
                'update_profile.change_password_form.validation.password_same',
                'New password cannot be same as current password'
            );
        }

        if (!passwordForm.confirmPassword?.trim()) {
            errors.confirmPassword = t(
                'update_profile.change_password_form.validation.confirm_password_required',
                'Please confirm your password'
            );
        } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            errors.confirmPassword = t(
                'update_profile.change_password_form.validation.passwords_not_match',
                'Passwords do not match'
            );
        }

        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };


    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (!validatePasswordForm()) return;

        try {
            await dispatch(changeAdminPassword({
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
                confirmPassword: passwordForm.confirmPassword
            })).unwrap();

            // Reset ONLY on success
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });

        } catch (err) {
            console.log(err);
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    useEffect(() => {
        setEditErrors({});
        setPasswordErrors({});
    }, [tabValue]);


    return (
        <Box sx={{ p: '36px 24px 24px' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px', mb: '24px', }} className='main_title'>
                {t('profile', 'Profile')}
            </Typography>

            {/* Profile Header Card */}
            <Box sx={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <Box sx={{ width: { xs: '100%', md: 'calc(40% - 24px)' }, }}>
                    <Card md={6} sx={{
                        borderRadius: '10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(230, 210, 249, 0.2)',
                        p: '20px'
                    }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Avatar
                                src={getFullUrl(profile?.profileImage || null)}
                                variant="square"
                                sx={{ width: 100, height: 100, border: '4px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px' }}
                            />
                            <Typography variant="h5" sx={{ mt: '12px', mb: '6px', color: '#fff', fontWeight: '600' }}>
                                {profile?.name || 'Admin'}
                            </Typography>
                            <Typography sx={{ color: '#B0B0B0' }}>
                                {profile?.email || 'admin@solfana.com'}
                            </Typography>
                        </Box>
                    </Card>
                </Box>

                <Box sx={{ width: { xs: '100%', md: '60%' }, }}>
                    {/* Tabs Section */}
                    <Card md={6} sx={{
                        borderRadius: '10px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(230, 210, 249, 0.2)',
                        mb: '20px',
                        p: '20px'
                    }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            sx={{
                                '& .MuiTabs-scroller': { overflowX: 'auto !important', },
                                '& .MuiTabs-flexContainer': { gap: '10px', },
                                '& .MuiTab-root': { color: 'white', fontWeight: '400', textTransform: 'none', fontSize: '15px', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '999px', padding: '8px 20px', minHeight: '42px', backgroundColor: 'rgba(230, 210, 249, 0.2)', },
                                '& .Mui-selected': { background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)', color: 'white !important', border: '0', },
                                '& .MuiTabs-indicator': { backgroundColor: 'transparent', display: 'none' }
                            }}
                        >
                            <Tab label={t('update_profile.edit_profile', 'Edit Profile')} />
                            <Tab label={t('update_profile.change_password', 'Change Password')} />
                        </Tabs>

                        <CardContent sx={{ px: '0', }}>
                            {tabValue === 0 && (
                                <Box component="form" onSubmit={handleEditSubmit}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Typography sx={{ color: '#B0B0B0', mb: 1, fontSize: '15px', lineHeight: '20px', fontWeight: '400' }}>{t('name', 'Name')} *</Typography>
                                            <TextField
                                                fullWidth
                                                name="name"
                                                value={editForm.name}
                                                onChange={handleEditChange}
                                                placeholder={t('update_profile.change_profile.fields.name')}
                                                variant="outlined"
                                                error={!!editErrors.name}
                                                helperText={editErrors.name}
                                                sx={{
                                                    mb: '20px',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '999px',
                                                        backgroundColor: 'transparent',
                                                        color: '#fff',
                                                        fontSize: '15px',
                                                        '& fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                            borderWidth: '1px',
                                                        },
                                                    },
                                                    '& .MuiOutlinedInput-input': {
                                                        padding: '12px 20px',
                                                        color: '#fff',
                                                    },
                                                    '& input::placeholder': {
                                                        color: 'rgba(255,255,255,0.6)',
                                                        opacity: 1,
                                                    },
                                                    '& .MuiFormHelperText-root': {
                                                        ml: '20px'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography sx={{ color: '#B0B0B0', mb: 1, fontSize: '15px', lineHeight: '20px', fontWeight: '400' }}>{t('phone', 'Phone')}</Typography>
                                            <TextField
                                                fullWidth
                                                name="phone"
                                                value={editForm.phone}
                                                onChange={handleEditChange}
                                                onKeyDown={(e) => {
                                                    if (
                                                        [
                                                            'Backspace',
                                                            'Delete',
                                                            'Tab',
                                                            'Escape',
                                                            'Enter',
                                                            'ArrowLeft',
                                                            'ArrowRight',
                                                            'ArrowUp',
                                                            'ArrowDown',
                                                            'Home',
                                                            'End',
                                                        ].includes(e.key) ||
                                                        (e.ctrlKey || e.metaKey)
                                                    ) {
                                                        return;
                                                    }
                                                    if (!/^[0-9]$/.test(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                inputProps={{
                                                    inputMode: 'numeric',
                                                    pattern: '[0-9]*',
                                                    maxLength: 10,
                                                }}
                                                placeholder={t('update_profile.change_profile.fields.phone')}
                                                variant="outlined"
                                                error={!!editErrors.phone}
                                                helperText={editErrors.phone}
                                                sx={{
                                                    mb: '20px',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '999px',
                                                        backgroundColor: 'transparent',
                                                        color: '#fff',
                                                        fontSize: '15px',
                                                        '& fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                            borderWidth: '1px',
                                                        },
                                                    },
                                                    '& .MuiOutlinedInput-input': {
                                                        padding: '12px 20px',
                                                        color: '#ff',
                                                    },
                                                    '& input::placeholder': {
                                                        color: 'rgba(255,255,255,0.6)',
                                                        opacity: 1,
                                                    },
                                                    '& .MuiFormHelperText-root': {
                                                        ml: '20px'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography sx={{ color: '#B0B0B0', mb: 1, fontSize: '15px', lineHeight: '20px', fontWeight: '400' }}>{t('update_profile.change_profile.fields.profile_image', 'Profile Image')}</Typography>
                                            <Box
                                                component="label"
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const file = e.dataTransfer.files[0];
                                                    if (file) {
                                                        handleImageChange({ target: { files: [file] } });
                                                    }
                                                }}
                                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                sx={{
                                                    backgroundColor: 'transparent',
                                                    color: '#fff',
                                                    border: editErrors.profileImage ? '1px solid #d32f2f' : '1px solid rgba(230, 210, 249, 0.2)',
                                                    borderRadius: '999px',
                                                    padding: '8px 20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    gap: 2,
                                                }}
                                            >
                                                <input type="file" hidden onChange={handleImageChange} accept="image/*" />
                                                <Box sx={{ pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Button
                                                        component="span"
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            color: '#fff',
                                                            textTransform: 'none',
                                                            boxShadow: 'none',
                                                            fontSize: '14px',
                                                            lineHeight: '16px',
                                                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)', boxShadow: 'none', }
                                                        }}
                                                    >
                                                        {t('update_profile.change_profile.fields.choose_file', 'Choose file')}
                                                    </Button>
                                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}>
                                                        {editForm.profileImage ? editForm.profileImage.name : t('no_file_chosen', 'No file chosen')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {/* <Typography sx={{ fontSize: '12px', color: '#ff4d4f', mt: 1, ml: '20px' }}>
                                                {t('administrator.fields.upload_square_only', '(Upload Only Square Image)')}
                                            </Typography> */}
                                            {editErrors.profileImage && (
                                                <Typography sx={{ color: '#d32f2f', fontSize: '12px', mt: 1, ml: '20px' }}>
                                                    {editErrors.profileImage}
                                                </Typography>
                                            )}
                                            {imagePreview && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Box
                                                        component="img"
                                                        src={imagePreview}
                                                        alt="preview"
                                                        sx={{ maxWidth: '100%', maxHeight: '200px', border: '2px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', objectFit: 'contain' }}
                                                    />
                                                </Box>
                                            )}
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={loading}
                                                sx={{
                                                    mt: 4,
                                                    py: 1.5,
                                                    borderRadius: '999px',
                                                    fontWeight: '400',
                                                    fontSize: '15px',
                                                    lineHeight: '24px',
                                                    textTransform: 'none',
                                                    background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                                                    textTransform: 'capitalize',
                                                    minWidth: '200px',
                                                }}
                                            >
                                                {loading ? t('updating') : t('update')}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {tabValue === 1 && (
                                <Box component="form" onSubmit={handlePasswordSubmit}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Typography sx={{ color: '#B0B0B0', mb: 1, fontSize: '15px', lineHeight: '20px', fontWeight: '400' }}>{t('update_profile.change_password_form.fields.current_password', 'Current Password')} *</Typography>
                                            <TextField
                                                fullWidth
                                                type={showPasswords.current ? 'text' : 'password'}
                                                name="currentPassword"
                                                value={passwordForm.currentPassword}
                                                onChange={handlePasswordChange}
                                                placeholder={t('update_profile.change_password_form.fields.current_password', 'Current Password')}
                                                error={!!passwordErrors.currentPassword}
                                                helperText={passwordErrors.currentPassword}
                                                sx={{
                                                    mb: '20px',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '999px',
                                                        backgroundColor: 'transparent',
                                                        color: '#fff',
                                                        fontSize: '15px',
                                                        '& fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                            borderWidth: '1px',
                                                        },
                                                    },
                                                    '& .MuiOutlinedInput-input': {
                                                        padding: '12px 20px',
                                                        color: '#fff',
                                                    },
                                                    '& input::placeholder': {
                                                        color: 'rgba(255,255,255,0.6)',
                                                        opacity: 1,
                                                    },
                                                    '& .MuiFormHelperText-root': {
                                                        ml: '20px'
                                                    }
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton type='button' onClick={() => togglePasswordVisibility('current')} edge="end" sx={{ mr: '0px' }}>
                                                                {!showPasswords.current ?
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M9.46992 15.28C9.27992 15.28 9.08992 15.21 8.93992 15.06C8.11992 14.24 7.66992 13.15 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C13.1499 7.67004 14.2399 8.12004 15.0599 8.94004C15.1999 9.08004 15.2799 9.27004 15.2799 9.47004C15.2799 9.67004 15.1999 9.86004 15.0599 10L9.99992 15.06C9.84992 15.21 9.65992 15.28 9.46992 15.28ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 12.5 9.29992 12.98 9.53992 13.4L13.3999 9.54004C12.9799 9.30004 12.4999 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                                                                        <path d="M5.59984 18.51C5.42984 18.51 5.24984 18.45 5.10984 18.33C4.03984 17.42 3.07984 16.3 2.25984 15C1.19984 13.35 1.19984 10.66 2.25984 8.99998C4.69984 5.17998 8.24984 2.97998 11.9998 2.97998C14.1998 2.97998 16.3698 3.73998 18.2698 5.16998C18.5998 5.41998 18.6698 5.88998 18.4198 6.21998C18.1698 6.54998 17.6998 6.61998 17.3698 6.36998C15.7298 5.12998 13.8698 4.47998 11.9998 4.47998C8.76984 4.47998 5.67984 6.41998 3.51984 9.80998C2.76984 10.98 2.76984 13.02 3.51984 14.19C4.26984 15.36 5.12984 16.37 6.07984 17.19C6.38984 17.46 6.42984 17.93 6.15984 18.25C6.01984 18.42 5.80984 18.51 5.59984 18.51Z" fill="#FF69B4" />
                                                                        <path d="M12.0001 21.02C10.6701 21.02 9.37006 20.75 8.12006 20.22C7.74006 20.06 7.56006 19.62 7.72006 19.24C7.88006 18.86 8.32006 18.68 8.70006 18.84C9.76006 19.29 10.8701 19.52 11.9901 19.52C15.2201 19.52 18.3101 17.58 20.4701 14.19C21.2201 13.02 21.2201 10.98 20.4701 9.81C20.1601 9.32 19.8201 8.85 19.4601 8.41C19.2001 8.09 19.2501 7.62 19.5701 7.35C19.8901 7.09 20.3601 7.13 20.6301 7.46C21.0201 7.94 21.4001 8.46 21.7401 9C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02Z" fill="#FF69B4" />
                                                                        <path d="M12.6901 16.27C12.3401 16.27 12.0201 16.02 11.9501 15.66C11.8701 15.25 12.1401 14.86 12.5501 14.79C13.6501 14.59 14.5701 13.67 14.7701 12.57C14.8501 12.16 15.2401 11.9 15.6501 11.97C16.0601 12.05 16.3301 12.44 16.2501 12.85C15.9301 14.58 14.5501 15.95 12.8301 16.27C12.7801 16.26 12.7401 16.27 12.6901 16.27Z" fill="#FF69B4" />
                                                                        <path d="M1.99994 22.75C1.80994 22.75 1.61994 22.68 1.46994 22.53C1.17994 22.24 1.17994 21.76 1.46994 21.47L8.93994 14C9.22994 13.71 9.70994 13.71 9.99994 14C10.2899 14.29 10.2899 14.77 9.99994 15.06L2.52994 22.53C2.37994 22.68 2.18994 22.75 1.99994 22.75Z" fill="#FF69B4" />
                                                                        <path d="M14.5302 10.2199C14.3402 10.2199 14.1502 10.1499 14.0002 9.99994C13.7102 9.70994 13.7102 9.22994 14.0002 8.93994L21.4702 1.46994C21.7602 1.17994 22.2402 1.17994 22.5302 1.46994C22.8202 1.75994 22.8202 2.23994 22.5302 2.52994L15.0602 9.99994C14.9102 10.1499 14.7202 10.2199 14.5302 10.2199Z" fill="#FF69B4" />
                                                                    </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M11.9999 16.33C9.60992 16.33 7.66992 14.39 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C14.3899 7.67004 16.3299 9.61004 16.3299 12C16.3299 14.39 14.3899 16.33 11.9999 16.33ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 13.56 10.4399 14.83 11.9999 14.83C13.5599 14.83 14.8299 13.56 14.8299 12C14.8299 10.44 13.5599 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                                                                        <path d="M12.0001 21.02C8.24008 21.02 4.69008 18.82 2.25008 15C1.19008 13.35 1.19008 10.66 2.25008 8.99998C4.70008 5.17998 8.25008 2.97998 12.0001 2.97998C15.7501 2.97998 19.3001 5.17998 21.7401 8.99998C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02ZM12.0001 4.47998C8.77008 4.47998 5.68008 6.41998 3.52008 9.80998C2.77008 10.98 2.77008 13.02 3.52008 14.19C5.68008 17.58 8.77008 19.52 12.0001 19.52C15.2301 19.52 18.3201 17.58 20.4801 14.19C21.2301 13.02 21.2301 10.98 20.4801 9.80998C18.3201 6.41998 15.2301 4.47998 12.0001 4.47998Z" fill="#FF69B4" />
                                                                    </svg>
                                                                }
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography sx={{ color: '#B0B0B0', mb: 1, fontSize: '15px', lineHeight: '20px', fontWeight: '400' }}>{t('update_profile.change_password_form.fields.new_password', 'New Password')} *</Typography>
                                            <TextField
                                                fullWidth
                                                type={showPasswords.new ? 'text' : 'password'}
                                                name="newPassword"
                                                value={passwordForm.newPassword}
                                                onChange={handlePasswordChange}
                                                placeholder={t('update_profile.change_password_form.fields.new_password', 'New Password')}
                                                error={!!passwordErrors.newPassword}
                                                helperText={passwordErrors.newPassword}
                                                required
                                                sx={{
                                                    mb: '20px',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '999px',
                                                        backgroundColor: 'transparent',
                                                        color: '#fff',
                                                        fontSize: '15px',
                                                        '& fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                            borderWidth: '1px',
                                                        },
                                                    },
                                                    '& .MuiOutlinedInput-input': {
                                                        padding: '12px 20px',
                                                        color: '#fff',
                                                    },
                                                    '& input::placeholder': {
                                                        color: 'rgba(255,255,255,0.6)',
                                                        opacity: 1,
                                                    },
                                                    '& .MuiFormHelperText-root': {
                                                        ml: '20px'
                                                    }
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton type='button' onClick={() => togglePasswordVisibility('new')} edge="end" sx={{ mr: '0px' }}>
                                                                {!showPasswords.new ?
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M9.46992 15.28C9.27992 15.28 9.08992 15.21 8.93992 15.06C8.11992 14.24 7.66992 13.15 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C13.1499 7.67004 14.2399 8.12004 15.0599 8.94004C15.1999 9.08004 15.2799 9.27004 15.2799 9.47004C15.2799 9.67004 15.1999 9.86004 15.0599 10L9.99992 15.06C9.84992 15.21 9.65992 15.28 9.46992 15.28ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 12.5 9.29992 12.98 9.53992 13.4L13.3999 9.54004C12.9799 9.30004 12.4999 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                                                                        <path d="M5.59984 18.51C5.42984 18.51 5.24984 18.45 5.10984 18.33C4.03984 17.42 3.07984 16.3 2.25984 15C1.19984 13.35 1.19984 10.66 2.25984 8.99998C4.69984 5.17998 8.24984 2.97998 11.9998 2.97998C14.1998 2.97998 16.3698 3.73998 18.2698 5.16998C18.5998 5.41998 18.6698 5.88998 18.4198 6.21998C18.1698 6.54998 17.6998 6.61998 17.3698 6.36998C15.7298 5.12998 13.8698 4.47998 11.9998 4.47998C8.76984 4.47998 5.67984 6.41998 3.51984 9.80998C2.76984 10.98 2.76984 13.02 3.51984 14.19C4.26984 15.36 5.12984 16.37 6.07984 17.19C6.38984 17.46 6.42984 17.93 6.15984 18.25C6.01984 18.42 5.80984 18.51 5.59984 18.51Z" fill="#FF69B4" />
                                                                        <path d="M12.0001 21.02C10.6701 21.02 9.37006 20.75 8.12006 20.22C7.74006 20.06 7.56006 19.62 7.72006 19.24C7.88006 18.86 8.32006 18.68 8.70006 18.84C9.76006 19.29 10.8701 19.52 11.9901 19.52C15.2201 19.52 18.3101 17.58 20.4701 14.19C21.2201 13.02 21.2201 10.98 20.4701 9.81C20.1601 9.32 19.8201 8.85 19.4601 8.41C19.2001 8.09 19.2501 7.62 19.5701 7.35C19.8901 7.09 20.3601 7.13 20.6301 7.46C21.0201 7.94 21.4001 8.46 21.7401 9C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02Z" fill="#FF69B4" />
                                                                        <path d="M12.6901 16.27C12.3401 16.27 12.0201 16.02 11.9501 15.66C11.8701 15.25 12.1401 14.86 12.5501 14.79C13.6501 14.59 14.5701 13.67 14.7701 12.57C14.8501 12.16 15.2401 11.9 15.6501 11.97C16.0601 12.05 16.3301 12.44 16.2501 12.85C15.9301 14.58 14.5501 15.95 12.8301 16.27C12.7801 16.26 12.7401 16.27 12.6901 16.27Z" fill="#FF69B4" />
                                                                        <path d="M1.99994 22.75C1.80994 22.75 1.61994 22.68 1.46994 22.53C1.17994 22.24 1.17994 21.76 1.46994 21.47L8.93994 14C9.22994 13.71 9.70994 13.71 9.99994 14C10.2899 14.29 10.2899 14.77 9.99994 15.06L2.52994 22.53C2.37994 22.68 2.18994 22.75 1.99994 22.75Z" fill="#FF69B4" />
                                                                        <path d="M14.5302 10.2199C14.3402 10.2199 14.1502 10.1499 14.0002 9.99994C13.7102 9.70994 13.7102 9.22994 14.0002 8.93994L21.4702 1.46994C21.7602 1.17994 22.2402 1.17994 22.5302 1.46994C22.8202 1.75994 22.8202 2.23994 22.5302 2.52994L15.0602 9.99994C14.9102 10.1499 14.7202 10.2199 14.5302 10.2199Z" fill="#FF69B4" />
                                                                    </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M11.9999 16.33C9.60992 16.33 7.66992 14.39 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C14.3899 7.67004 16.3299 9.61004 16.3299 12C16.3299 14.39 14.3899 16.33 11.9999 16.33ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 13.56 10.4399 14.83 11.9999 14.83C13.5599 14.83 14.8299 13.56 14.8299 12C14.8299 10.44 13.5599 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                                                                        <path d="M12.0001 21.02C8.24008 21.02 4.69008 18.82 2.25008 15C1.19008 13.35 1.19008 10.66 2.25008 8.99998C4.70008 5.17998 8.25008 2.97998 12.0001 2.97998C15.7501 2.97998 19.3001 5.17998 21.7401 8.99998C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02ZM12.0001 4.47998C8.77008 4.47998 5.68008 6.41998 3.52008 9.80998C2.77008 10.98 2.77008 13.02 3.52008 14.19C5.68008 17.58 8.77008 19.52 12.0001 19.52C15.2301 19.52 18.3201 17.58 20.4801 14.19C21.2301 13.02 21.2301 10.98 20.4801 9.80998C18.3201 6.41998 15.2301 4.47998 12.0001 4.47998Z" fill="#FF69B4" />
                                                                    </svg>
                                                                }
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography sx={{ color: '#B0B0B0', mb: 1, fontSize: '15px', lineHeight: '20px', fontWeight: '400' }}>{t('update_profile.change_password_form.fields.confirm_password', 'Confirm Password')} *</Typography>
                                            <TextField
                                                fullWidth
                                                type={showPasswords.confirm ? 'text' : 'password'}
                                                name="confirmPassword"
                                                value={passwordForm.confirmPassword}
                                                onChange={handlePasswordChange}
                                                placeholder={t('update_profile.change_password_form.fields.confirm_password', 'Confirm Password')}
                                                error={!!passwordErrors.confirmPassword}
                                                helperText={passwordErrors.confirmPassword}
                                                required
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '999px',
                                                        backgroundColor: 'transparent',
                                                        color: '#fff',
                                                        fontSize: '15px',
                                                        '& fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'rgba(230, 210, 249, 0.2)',
                                                            borderWidth: '1px',
                                                        },
                                                    },
                                                    '& .MuiOutlinedInput-input': {
                                                        padding: '12px 20px',
                                                        color: '#fff',
                                                    },
                                                    '& input::placeholder': {
                                                        color: 'rgba(255,255,255,0.6)',
                                                        opacity: 1,
                                                    },
                                                    '& .MuiFormHelperText-root': {
                                                        ml: '20px'
                                                    }
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton type='button' onClick={() => togglePasswordVisibility('confirm')} edge="end" sx={{ mr: '0px' }}>
                                                                {!showPasswords.confirm ?
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M9.46992 15.28C9.27992 15.28 9.08992 15.21 8.93992 15.06C8.11992 14.24 7.66992 13.15 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C13.1499 7.67004 14.2399 8.12004 15.0599 8.94004C15.1999 9.08004 15.2799 9.27004 15.2799 9.47004C15.2799 9.67004 15.1999 9.86004 15.0599 10L9.99992 15.06C9.84992 15.21 9.65992 15.28 9.46992 15.28ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 12.5 9.29992 12.98 9.53992 13.4L13.3999 9.54004C12.9799 9.30004 12.4999 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                                                                        <path d="M5.59984 18.51C5.42984 18.51 5.24984 18.45 5.10984 18.33C4.03984 17.42 3.07984 16.3 2.25984 15C1.19984 13.35 1.19984 10.66 2.25984 8.99998C4.69984 5.17998 8.24984 2.97998 11.9998 2.97998C14.1998 2.97998 16.3698 3.73998 18.2698 5.16998C18.5998 5.41998 18.6698 5.88998 18.4198 6.21998C18.1698 6.54998 17.6998 6.61998 17.3698 6.36998C15.7298 5.12998 13.8698 4.47998 11.9998 4.47998C8.76984 4.47998 5.67984 6.41998 3.51984 9.80998C2.76984 10.98 2.76984 13.02 3.51984 14.19C4.26984 15.36 5.12984 16.37 6.07984 17.19C6.38984 17.46 6.42984 17.93 6.15984 18.25C6.01984 18.42 5.80984 18.51 5.59984 18.51Z" fill="#FF69B4" />
                                                                        <path d="M12.0001 21.02C10.6701 21.02 9.37006 20.75 8.12006 20.22C7.74006 20.06 7.56006 19.62 7.72006 19.24C7.88006 18.86 8.32006 18.68 8.70006 18.84C9.76006 19.29 10.8701 19.52 11.9901 19.52C15.2201 19.52 18.3101 17.58 20.4701 14.19C21.2201 13.02 21.2201 10.98 20.4701 9.81C20.1601 9.32 19.8201 8.85 19.4601 8.41C19.2001 8.09 19.2501 7.62 19.5701 7.35C19.8901 7.09 20.3601 7.13 20.6301 7.46C21.0201 7.94 21.4001 8.46 21.7401 9C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02Z" fill="#FF69B4" />
                                                                        <path d="M12.6901 16.27C12.3401 16.27 12.0201 16.02 11.9501 15.66C11.8701 15.25 12.1401 14.86 12.5501 14.79C13.6501 14.59 14.5701 13.67 14.7701 12.57C14.8501 12.16 15.2401 11.9 15.6501 11.97C16.0601 12.05 16.3301 12.44 16.2501 12.85C15.9301 14.58 14.5501 15.95 12.8301 16.27C12.7801 16.26 12.7401 16.27 12.6901 16.27Z" fill="#FF69B4" />
                                                                        <path d="M1.99994 22.75C1.80994 22.75 1.61994 22.68 1.46994 22.53C1.17994 22.24 1.17994 21.76 1.46994 21.47L8.93994 14C9.22994 13.71 9.70994 13.71 9.99994 14C10.2899 14.29 10.2899 14.77 9.99994 15.06L2.52994 22.53C2.37994 22.68 2.18994 22.75 1.99994 22.75Z" fill="#FF69B4" />
                                                                        <path d="M14.5302 10.2199C14.3402 10.2199 14.1502 10.1499 14.0002 9.99994C13.7102 9.70994 13.7102 9.22994 14.0002 8.93994L21.4702 1.46994C21.7602 1.17994 22.2402 1.17994 22.5302 1.46994C22.8202 1.75994 22.8202 2.23994 22.5302 2.52994L15.0602 9.99994C14.9102 10.1499 14.7202 10.2199 14.5302 10.2199Z" fill="#FF69B4" />
                                                                    </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                        <path d="M11.9999 16.33C9.60992 16.33 7.66992 14.39 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C14.3899 7.67004 16.3299 9.61004 16.3299 12C16.3299 14.39 14.3899 16.33 11.9999 16.33ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 13.56 10.4399 14.83 11.9999 14.83C13.5599 14.83 14.8299 13.56 14.8299 12C14.8299 10.44 13.5599 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                                                                        <path d="M12.0001 21.02C8.24008 21.02 4.69008 18.82 2.25008 15C1.19008 13.35 1.19008 10.66 2.25008 8.99998C4.70008 5.17998 8.25008 2.97998 12.0001 2.97998C15.7501 2.97998 19.3001 5.17998 21.7401 8.99998C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02ZM12.0001 4.47998C8.77008 4.47998 5.68008 6.41998 3.52008 9.80998C2.77008 10.98 2.77008 13.02 3.52008 14.19C5.68008 17.58 8.77008 19.52 12.0001 19.52C15.2301 19.52 18.3201 17.58 20.4801 14.19C21.2301 13.02 21.2301 10.98 20.4801 9.80998C18.3201 6.41998 15.2301 4.47998 12.0001 4.47998Z" fill="#FF69B4" />
                                                                    </svg>
                                                                }
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                disabled={loading}
                                                sx={{
                                                    mt: 4,
                                                    py: 1.5,
                                                    borderRadius: '999px',
                                                    fontWeight: '400',
                                                    fontSize: '15px',
                                                    lineHeight: '24px',
                                                    textTransform: 'none',
                                                    background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                                                    textTransform: 'capitalize',
                                                    minWidth: '200px',
                                                }}
                                            >
                                                {loading ? t('updating', 'Updating...') : t('update_profile.change_password_form.actions.update_password', 'Update Password')}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
}

export default Profile;
